import { useMemo } from 'react';
import { addDays, todayISO } from '@shared/dates';
import {
  useEvents,
  useMicrocycles,
  usePlayers,
  useSeasons,
  useSessions,
  useTeams,
} from '@/app/queries';
import { START_STEPS, type StartStepKey } from '@/routes/guide/pages';

/**
 * How far through setting up a coach actually is.
 *
 * Every check is a fact about their own data, never a flag set when they
 * visited a screen: a coach who imported players elsewhere still gets the tick,
 * and a tick that is not true is visible immediately.
 *
 * This lives here rather than inside the Guide because two screens ask the
 * question — the Guide's "Start here" and the dashboard's first panel — and two
 * copies of "has he built a week yet" would answer differently the first time
 * either changed. TanStack Query dedupes the six requests, so the second caller
 * costs nothing.
 */
export type StartProgress = {
  readonly state: Readonly<Record<StartStepKey, boolean>>;
  readonly done: number;
  readonly total: number;
  /** The first step not yet done, or null when there is nothing left to do. */
  readonly next: StartStepKey | null;
  readonly complete: boolean;
  /** False until the answers can be trusted, so nothing renders a wrong tick. */
  readonly ready: boolean;
};

export function useStartProgress(): StartProgress {
  const teams = useTeams();
  const teamList = teams.data ?? [];
  const teamId = teamList[0]?.id;

  const players = usePlayers(teamId);
  const seasons = useSeasons(teamId);
  const from = addDays(todayISO(), -120);
  const to = addDays(todayISO(), 240);
  const events = useEvents(teamId, from, to);
  const cycles = useMicrocycles(teamId, from, to);
  const sessions = useSessions(teamId, from, to);

  const ready =
    teams.isSuccess &&
    (!teamId ||
      (players.isSuccess &&
        seasons.isSuccess &&
        events.isSuccess &&
        cycles.isSuccess &&
        sessions.isSuccess));

  return useMemo(() => {
    const squad = players.data ?? [];
    const allEvents = events.data ?? [];
    const allSessions = sessions.data ?? [];

    const state = {
      team: teamList.length > 0,
      season: (seasons.data ?? []).length > 0,
      squad: squad.length > 0,
      roles: squad.some((p) => p.roleKey) && squad.some((p) => p.primaryPositionKey),
      calendar: allEvents.some((e) => e.kind === 'match'),
      week: (cycles.data ?? []).length > 0,
      // Built and run: a session that carries an actual rating, not just a plan.
      session: allSessions.some((s) => s.actualRpe !== null && s.actualRpe !== undefined),
      // A match that has been played. Evaluations are a different feature and
      // ticking this off for one would be a lie the coach can see.
      match: allEvents.some((e) => e.kind === 'match' && e.onDate < todayISO()),
    } satisfies Record<StartStepKey, boolean>;

    const done = START_STEPS.filter((s) => state[s.key]).length;
    const next = START_STEPS.find((s) => !state[s.key])?.key ?? null;

    return {
      state,
      done,
      total: START_STEPS.length,
      next,
      complete: done === START_STEPS.length,
      ready,
    };
  }, [
    teamList.length,
    players.data,
    seasons.data,
    events.data,
    cycles.data,
    sessions.data,
    ready,
  ]);
}
